import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Badge, Plate, Radio } from '@alfa-retail-react/components';
import classNames from 'classnames';
import Big from 'big.js';

import { useIsDesktop } from 'app/utils/useWindowType';
import { FormatUtils } from 'app/utils/FormatUtils';
import { Section } from 'app/component/Section';
import { ProductParametersConfigurePageStore } from 'app/logic/pages/productParametersConfigure/ProductParametersConfigurePageStore';
import { useI18nContext } from 'app/store/сontext/I18nContext';
import { useFormContext } from '@alfa-retail-react/components/form-hook';
import { ProductParametersConfigureFormValues } from './ProductParametersConfigurePage';
import {
    DownloadResourceLink,
    OpenResourceMode,
} from '../../../component/DownloadResourceLink';
import { ResourceFormat } from '../../../model/ResourceFormat';
import Button from '@alfa-retail-react/components/button';
import { Controller, useWatch } from 'react-hook-form';
import { YesNoState } from '@alfa-retail-react/components/yes-no-question';
import { AlertCircleMIcon } from '@alfalab/icons-glyph/AlertCircleMIcon';
import { InsuranceProduct } from 'app/model/product/InsuranceProduct';

export interface InsuranceFormProps {
    pageStore: ProductParametersConfigurePageStore;
}

const WITHOUT_INSURANCE_CODE = 'WITHOUT_INSURANCE';

enum FormNames {
    INSURANCE = 'insurance',
    LIMIT = 'limit',
}

type InsuranceChoiceType = {
    code: string | null;
    title: string;
    description?: string;
    descriptions?: string[];
    link?: {
        name: string;
        url: string;
    };
    background?: string;
};

type ListProps = Pick<InsuranceChoiceType, 'description' | 'descriptions'>;

const List = ({ description, descriptions }: ListProps) =>
    Array.isArray(descriptions) ? (
        <ul>
            {descriptions.map((item, index) => (
                <li key={index}>{item}</li>
            ))}
        </ul>
    ) : (
        <span>{description}</span>
    );

/**
 * Форма выбора необходимости страховки.
 * (первая и единственная в списке insurance[0])
 */
export const InsuranceFormV2 = observer(({ pageStore }: InsuranceFormProps) => {
    const { formStore } = pageStore;
    const { insuranceStore } = formStore;
    const [isDesktop] = useIsDesktop();
    const { t } = useI18nContext() as { t: (key: string | string[]) => string };

    const { control, trigger, formState, clearErrors } =
        useFormContext<ProductParametersConfigureFormValues>();
    const isLimitTouched =
        formState.isSubmitted || !!formState.touchedFields[FormNames.LIMIT];
    const { hasAppliedInsurance } = formStore;

    /**
     * Минимальный лимит зависит от выбора страховки (LimitForm),
     * поэтому после смены выбора перевалидируем лимит уже с новыми правилами.
     */
    useEffect(() => {
        if (isLimitTouched) {
            trigger(FormNames.LIMIT);
        }
    }, [hasAppliedInsurance]);

    const insuranceInfo = insuranceStore.getInsuranceByIndex(0);

    const [insuranceChoiceValue, limitValue] = useWatch({
        control,
        name: [FormNames.INSURANCE, FormNames.LIMIT],
    });

    const {
        code: insuranceCode = null,
        currency,
        amount,
    } = insuranceInfo ?? ({} as Partial<InsuranceProduct>);

    const {
        [FormNames.INSURANCE]: insuranceError,
        [FormNames.LIMIT]: limitError,
    } = formState.errors;

    const insuranceChoiceArray: InsuranceChoiceType[] = useMemo(() => {
        return [
            {
                code: insuranceCode,
                title: t('redCard_terms_insuranceBlock_on_list_title'),
                descriptions: [
                    t('redCard_terms_insuranceBlock_on_list_item0'),
                    t('redCard_terms_insuranceBlock_on_list_item1'),
                    t('redCard_terms_insuranceBlock_on_list_item2'),
                ],
                link: {
                    url: '',
                    name: t('redCard_terms_button_insuranceTerms'),
                },
                background: 'linear-gradient(to bottom left,#D7E9FF,#F6F0FF)',
            },
            {
                code: WITHOUT_INSURANCE_CODE,
                title: t('redCard_terms_insuranceBlock_off_list_title'),
                description: t('redCard_terms_insuranceBlock_off_list_item0'),
                background: '#F3F4F5',
            },
        ];
    }, [insuranceCode, t]);

    const calculatedSums = useMemo(() => {
        const canCalculate =
            !!limitValue &&
            !!insuranceChoiceValue &&
            !insuranceError &&
            !limitError;
        const withInsurance = insuranceChoiceValue !== WITHOUT_INSURANCE_CODE;

        const totalSum = new Big(canCalculate ? limitValue : 0);
        const insuranceFee =
            canCalculate && withInsurance && amount ? amount : new Big(0);
        const sumOnHand = totalSum.minus(insuranceFee);

        return [
            {
                title: t('redCard_terms_insuranceBlock_total_sum'),
                value: FormatUtils.toIntegerString(totalSum),
            },
            {
                title: t('redCard_terms_insuranceBlock_productTitle'),
                value: FormatUtils.toIntegerString(insuranceFee),
            },
            {
                title: t('redCard_terms_insuranceBlock_on_hands_sum'),
                value: FormatUtils.toIntegerString(sumOnHand),
            },
        ];
    }, [
        limitValue,
        insuranceChoiceValue,
        insuranceError,
        limitError,
        amount,
        currency,
        t,
    ]);

    const handleChangeChoice = (
        value: string | null,
        onChange: (value: string | null) => void,
    ) => {
        clearErrors(FormNames.INSURANCE);
        if (value && value !== WITHOUT_INSURANCE_CODE) {
            formStore.setHasInsurance(YesNoState.YES);
            formStore.setInsurance(insuranceInfo);
        } else {
            formStore.setHasInsurance(YesNoState.NO);
        }
        onChange(value);
    };

    return (
        <Section
            className={'newInsuranceFormSection'}
            content={
                <>
                    <div className={'radio-group-flex'}>
                        <Controller
                            control={control}
                            name={FormNames.INSURANCE}
                            defaultValue={null}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <>
                                    {insuranceChoiceArray.map((choice) => (
                                        <div
                                            key={choice.title}
                                            className={classNames(
                                                'radioWrapper',
                                                formState?.errors?.[
                                                    FormNames.INSURANCE
                                                ] && 'radioBlockError',
                                                !isDesktop && 'mobile',
                                            )}
                                            style={{
                                                background: choice.background,
                                            }}
                                        >
                                            <Radio
                                                circleClassName={'circleRadio'}
                                                className={'radioInner'}
                                                value={choice.code}
                                                onChange={(event) => {
                                                    handleChangeChoice(
                                                        choice.code,
                                                        field.onChange,
                                                    );
                                                }}
                                                checked={
                                                    field.value === choice.code
                                                }
                                                label={
                                                    <span
                                                        className={'radioLabel'}
                                                    >
                                                        {choice.title}
                                                    </span>
                                                }
                                                block={true}
                                                hint={
                                                    <>
                                                        <List
                                                            description={
                                                                choice.description
                                                            }
                                                            descriptions={
                                                                choice.descriptions
                                                            }
                                                        />

                                                        {choice?.link?.name &&
                                                            insuranceInfo && (
                                                                <DownloadResourceLink
                                                                    className={
                                                                        'pdfDocButton'
                                                                    }
                                                                    openResourceMode={
                                                                        OpenResourceMode.NEW_TAB
                                                                    }
                                                                    trigger={
                                                                        <Button
                                                                            view="secondary"
                                                                            size={
                                                                                'xxs'
                                                                            }
                                                                            href={
                                                                                '#'
                                                                            }
                                                                            onClick={(
                                                                                event: React.MouseEvent<
                                                                                    | HTMLAnchorElement
                                                                                    | HTMLButtonElement
                                                                                >,
                                                                            ) =>
                                                                                event.preventDefault()
                                                                            }
                                                                        >
                                                                            {
                                                                                choice
                                                                                    .link
                                                                                    .name
                                                                            }
                                                                        </Button>
                                                                    }
                                                                    title={
                                                                        choice
                                                                            .link
                                                                            .name
                                                                    }
                                                                    download={() =>
                                                                        pageStore.downloadInsuranceInstructions(
                                                                            insuranceInfo.code,
                                                                            insuranceInfo.conditionsDocumentCode,
                                                                            ResourceFormat.PDF,
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                    </>
                                                }
                                            />
                                        </div>
                                    ))}
                                </>
                            )}
                        />
                        {formState?.errors?.[FormNames.INSURANCE] && (
                            <Plate
                                className={'formValidationErrorPlate'}
                                view="negative"
                                customLeftAddons={
                                    <Badge
                                        view="icon"
                                        iconColor="negative"
                                        content={<AlertCircleMIcon />}
                                    />
                                }
                            >
                                Выберите один из вариантов
                            </Plate>
                        )}
                    </div>
                    <div
                        className={classNames(
                            'calculatedSumBlock',
                            !isDesktop && 'mobile',
                        )}
                    >
                        {calculatedSums.map(({ title, value }) => (
                            <div key={title} className={'calculatedSum'}>
                                <span className={'calculatedSumTitle'}>
                                    {title}
                                </span>
                                <span className={'calculatedSumValue'}>
                                    {value} <span>{currency}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className={'calculatedSumDescription'}>
                        {t('redCard_terms_insuranceBlock_final_description')}
                    </div>
                </>
            }
        />
    );
});
